import { CircularLoading } from '@foxtaibox/foxtailbox-frontend-core';
import { Badge, Box, Button, IconButton, styled, Typography } from '@mui/material';
import BinIcon from 'assets/icons/Bin';
import PauseIcon from 'assets/icons/Pause';
import PlayIcon from 'assets/icons/Play';
import RecordIcon from 'assets/icons/Record';
import StopRecordIcon from 'assets/icons/StopRecord';
import { RecordStatus, UseVoiceRecordedReturn } from 'hooks/useVoiceRecorded';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { secondsToHHMMSSFormat } from 'utils/secondsToMMSSFormat';

const StyledIconButton = styled(IconButton)({
  padding: 4,
  '& svg': { fontSize: 32 },
});

interface VoiceRecordButtonProps {
  voiceRecorded: UseVoiceRecordedReturn;
  onBeforeRemove?: (removeVoiceRecord: () => void) => void;
  dense?: boolean;
}

const VoiceRecordButton = ({ voiceRecorded, onBeforeRemove, dense }: VoiceRecordButtonProps) => {
  const {
    audioSrc,
    status,
    audioRef,
    timer,
    startRecord,
    stopRecord,
    play,
    pause,
    removeRecorder,
  } = voiceRecorded;
  const { t } = useTranslation();

  const {
    field: { onChange },
  } = useController({
    name: 'voiceRecord',
  });

  const handleRemove = () => {
    const removeVoiceRecord = () => {
      removeRecorder(onChange);
    };

    if (onBeforeRemove) {
      onBeforeRemove(removeVoiceRecord);
      return;
    }

    removeVoiceRecord();
  };

  const handleVoiceButton = () => {
    switch (status) {
      case RecordStatus.Ready:
      case RecordStatus.NotAllowed:
        startRecord(onChange);
        break;
      case RecordStatus.Recording:
        stopRecord();
        break;
      case RecordStatus.Done:
      case RecordStatus.Pause:
        play();
        break;
      case RecordStatus.Playing:
        pause();
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      {status === RecordStatus.Ready ||
      status === RecordStatus.NotAllowed ||
      status === RecordStatus.Waiting ? (
        <Button
          color='secondary'
          sx={{ height: 46 }}
          fullWidth
          size='large'
          variant={dense ? 'text' : 'contained'}
          loading={status === RecordStatus.Waiting}
          onClick={handleVoiceButton}>
          <RecordIcon
            sx={{
              position: 'absolute',
              top: '50%',
              left: 14,
              transform: 'translateY(-50%)',
              fontSize: 32,
            }}
          />
          {!dense && (
            <>
              {status === RecordStatus.Ready && t('assessment.question.voice.record')}
              {status === RecordStatus.NotAllowed &&
                t('assessment.question.voice.recordNotAllowed')}
            </>
          )}
        </Button>
      ) : (
        <Button
          color='secondary'
          sx={{
            height: 46,
            justifyContent: 'space-between',
            ':hover': { backgroundColor: 'bgSwitch' },
            minWidth: 200,
          }}
          fullWidth
          size='large'
          variant='contained'
          component='div'>
          {status === RecordStatus.Converting ? (
            <CircularLoading size={24} sx={{ py: 2.5, px: 1 }} />
          ) : (
            <StyledIconButton onClick={handleVoiceButton}>
              {status === RecordStatus.Recording && <StopRecordIcon />}
              {(status === RecordStatus.Done || status === RecordStatus.Pause) && <PlayIcon />}
              {status === RecordStatus.Playing && <PauseIcon />}
            </StyledIconButton>
          )}
          <Badge
            color='error'
            anchorOrigin={{ horizontal: 'left' }}
            variant='dot'
            slotProps={{
              badge: {
                style: {
                  top: '50%',
                  display: status === RecordStatus.Recording ? 'flex' : 'none',
                },
              },
            }}>
            <Typography sx={{ ml: status === RecordStatus.Recording ? 1.5 : 0 }}>
              {secondsToHHMMSSFormat(timer)}
            </Typography>
          </Badge>
          <StyledIconButton disabled={status === RecordStatus.Converting} onClick={handleRemove}>
            <BinIcon />
          </StyledIconButton>
        </Button>
      )}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio src={audioSrc} ref={audioRef} onEnded={handleVoiceButton} />
    </Box>
  );
};

export default VoiceRecordButton;
